<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import StatusPill from '../StatusPill.vue'
import type { AgentChannelMessage } from '../../stores/agent_channels'
import { formatAbsoluteDate, formatDate } from '../../composables/useFormattedDate'
import { NEUTRAL_PILL_CLASS } from '../../composables/statusPillColors'
import { isOwnerSource, sourceLabel } from '../../lib/interAgentTurn'

const props = defineProps<{
  message: AgentChannelMessage
  showHeader: boolean
  now: Date
  sessionNames?: Record<string, string>
}>()

const sessionLabel = computed(() => {
  const id = props.message.sender_thread_id
  return id ? (props.sessionNames?.[id] ?? props.message.sender_name) : null
})
const fromOwner = computed(() => isOwnerSource(props.message.source))
</script>

<template>
  <li
    class="max-w-[85%] flex flex-col gap-1"
    :class="fromOwner ? 'self-end items-end' : 'self-start items-start'"
    data-testid="channel-message"
  >
    <div v-if="showHeader" class="flex items-center gap-2 text-xs text-white/60">
      <RouterLink
        v-if="message.sender_thread_id"
        :to="`/agent/${message.sender_thread_id}`"
        class="text-brand-primary/90 font-medium hover:underline"
        data-testid="message-sender"
        >{{ sessionLabel }}</RouterLink
      >
      <span v-else class="text-white/80 font-medium" data-testid="message-sender">{{ message.sender_name }}</span>
      <StatusPill
        :label="sourceLabel(message.source)"
        :color-class="fromOwner ? 'bg-brand-primary/15 text-brand-primary/90' : NEUTRAL_PILL_CLASS"
        data-testid="message-source"
      />
      <time :datetime="message.created_at" :title="formatAbsoluteDate(message.created_at)">{{
        formatDate(message.created_at, now)
      }}</time>
    </div>
    <div
      class="rounded border px-3 py-2 text-sm text-white/90 whitespace-pre-wrap break-words"
      :class="fromOwner ? 'bg-brand-primary/10 border-brand-primary/30' : 'bg-white/5 border-white/10'"
    >
      {{ message.body }}
    </div>
  </li>
</template>
